import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PopupAlertType } from '../constants';
import { PopupAlertState, usePopupAlertsReducer } from '../store/reducers';
import { getAxiosErrorMessage } from '../utils/getAxiosErrorMessage';

type successMessageType<T> = ((data: T) => string) | string;
type errorMessageType = ((error: AxiosError) => string) | string;

interface HandleServerResponseProps<T> {
  data: T;
  error: AxiosError;
  successMessage?: successMessageType<T>;
  errorMessage?: errorMessageType;
  onSuccess?: (
    data: T,
    showAlert: (c: PopupAlertState) => void,
    navigate: (url: string) => void,
  ) => void;
  onError?: (error: AxiosError) => void;
}

export function useHandleServerResponse<T>({
  error,
  data,
  successMessage,
  errorMessage,
  onSuccess,
  onError,
}: HandleServerResponseProps<T>) {
  const navigate = useNavigate();
  const { showAlert } = usePopupAlertsReducer();

  useEffect(() => {
    if (!error) return;

    const serverMessage = getAxiosErrorMessage(error);

    const message = errorMessage || serverMessage;

    if (message) {
      showAlert({
        variant: PopupAlertType.Error,
        content: typeof message === 'function' ? message(error) : message,
      });
    }

    if (!onError) {
      return;
    }

    onError(error);
  }, [error]);

  useEffect(() => {
    if (!data) return;

    if (successMessage) {
      showAlert({
        content:
          typeof successMessage === 'function'
            ? successMessage(data)
            : successMessage,
      });
    }

    if (!onSuccess) {
      return;
    }

    onSuccess(data, showAlert, navigate);
  }, [data]);
}
